import React, { useState, useEffect, useCallback } from 'react';
import { Spin } from 'antd';
import {
    ThunderboltOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    CompassOutlined,
    CalendarOutlined,
    FieldTimeOutlined,
    DashboardOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    ComposedChart,
    BarChart,
    Bar,
    AreaChart,
    Area,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
} from 'recharts';
import moment from 'moment';
import { useReports } from '../context/ReportsContext';
import { getForecastsReport } from '../api/reportsApi';
import { TabHelpButton } from '../components/TabHelpGuideModal';

// Brand palette
const C = {
    blue: '#00AAF1',
    navy: '#003263',
    green: '#10b981',
    amber: '#f59e0b',
    red: '#ef4444',
    purple: '#a855f7',
    indigo: '#6366f1',
    slate: '#64748b',
};

const card = 'bg-white dark:bg-black p-6 rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs transition duration-200';
const tooltipStyle = {
    contentStyle: { background: '#0a0a0a', border: '1px solid #262626', borderRadius: 12, fontSize: 12, color: '#fff', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.5)' },
    labelStyle: { color: '#a7c8ff', fontWeight: 700, marginBottom: 4 },
};
const axisTick = { fontSize: 12, fill: '#94a3b8' };

export default function ForecastsTab() {
    const { filters, refreshKey, getCachedTab, setCachedTab } = useReports();
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(() => getCachedTab('forecasts'));

    const loadData = useCallback(async () => {
        const cached = getCachedTab('forecasts');
        if (cached) {
            setReportData(cached);
            return;
        }

        setLoading(true);
        try {
            const res = await getForecastsReport({
                department: filters.department,
                projectId: filters.projectId,
            });
            if (res && res.success && res.result) {
                setReportData(res.result);
                setCachedTab('forecasts', res.result);
            }
        } catch (err) {
            console.error('Error fetching forecasts report:', err);
        } finally {
            setLoading(false);
        }
    }, [filters.department, filters.projectId, refreshKey, getCachedTab, setCachedTab]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Active forecasts from API
    const forecasts = reportData?.forecasts || [];

    const highCount = forecasts.filter(f => (typeof f.confidenceScore === 'number' ? f.confidenceScore >= 90 : f.confidenceScore === 'High')).length;
    const medCount = forecasts.filter(f => (typeof f.confidenceScore === 'number' ? f.confidenceScore >= 70 && f.confidenceScore < 90 : f.confidenceScore === 'Medium')).length;
    const lowCount = forecasts.filter(f => (typeof f.confidenceScore === 'number' ? f.confidenceScore < 70 : f.confidenceScore === 'Low')).length;

    const confidencePieData = [
        { name: 'High Confidence (>90%)', value: highCount, fill: C.green },
        { name: 'Moderate Variance (70–90%)', value: medCount, fill: C.blue },
        { name: 'Low / At Risk (<70%)', value: lowCount, fill: C.amber },
    ].filter(d => d.value > 0);

    // Variance Cone Chart Data (Optimistic vs P50 vs P80)
    const varianceConeData = forecasts.map(f => {
        const shortName = f.projectName?.length > 20 ? `${f.projectName.slice(0, 18)}...` : f.projectName;
        return {
            name: shortName,
            fullName: f.projectName,
            optimisticDays: f.optimisticDays || Math.max(1, Math.round((f.varianceDays || 2) * 0.5)),
            p50Days: f.p50Days || (f.varianceDays || 3),
            p80Days: f.p80Days || Math.round((f.varianceDays || 3) * 1.6),
            progress: f.currentProgress || 0,
            riskScore: f.riskScore || 20,
            confidence: f.confidenceScore || 85,
            targetDueDate: f.targetDueDate ? moment(f.targetDueDate).format('MMM D, YYYY') : 'Not Set',
            predictedDate: f.predictedCompletionDate ? moment(f.predictedCompletionDate).format('MMM D, YYYY') : 'Calculating',
            bottlenecks: f.bottlenecks || 'None detected',
        };
    });

    // Custom Tooltip for Variance Cone
    const ConeTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null;
        const d = payload[0]?.payload;
        if (!d) return null;
        return (
            <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-3.5 shadow-2xl text-xs space-y-2 max-w-xs">
                <div className="font-bold text-white text-sm border-b border-neutral-800 pb-1">
                    {d.fullName}
                </div>
                <div className="grid grid-cols-2 gap-2 text-slate-300">
                    <div>Target Due: <span className="text-white font-semibold">{d.targetDueDate}</span></div>
                    <div>Predicted: <span className="text-blue-400 font-semibold">{d.predictedDate}</span></div>
                    <div>Model Confidence: <span className="text-emerald-400 font-bold">{d.confidence}%</span></div>
                    <div>Progress: <span className="text-cyan-400 font-bold">{d.progress}%</span></div>
                </div>
                <div className="pt-1.5 border-t border-neutral-800 space-y-1">
                    <div className="flex justify-between text-slate-400">
                        <span>P80 Conservative Slip:</span>
                        <span className="text-amber-400 font-bold">+{d.p80Days} days</span>
                    </div>
                    <div className="flex justify-between text-slate-400">
                        <span>P50 Expected Slip:</span>
                        <span className="text-blue-400 font-bold">+{d.p50Days} days</span>
                    </div>
                    <div className="flex justify-between text-slate-400">
                        <span>Optimistic Velocity:</span>
                        <span className="text-emerald-400 font-bold">+{d.optimisticDays} days</span>
                    </div>
                </div>
                <div className="text-[11px] text-slate-400 italic pt-1 border-t border-neutral-800">
                    ⚠ {d.bottlenecks}
                </div>
            </div>
        );
    };



    return (
        <Spin spinning={loading} size="large" tip="Computing forecasts & delivery projections...">
            <div className="space-y-6 min-h-[400px]">
            {/* Header info banner */}
            <div className="p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-black dark:to-neutral-950 rounded-2xl border border-blue-200/70 dark:border-neutral-800 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-xs">
                        <ThunderboltOutlined className="text-lg" />
                    </div>
                    <div>
                        <h3 className="text-sm sm:text-base font-extrabold text-[#003263] dark:text-[#a7c8ff] font-headline">
                            Predictive Delivery &amp; Milestone Velocity Forecasts
                        </h3>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                            Confidence cones calculated via Monte Carlo velocity simulation, dependency chains, and review latency.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400 px-3 py-1.5 rounded-xl bg-white dark:bg-black border border-slate-200 dark:border-neutral-800">
                        {forecasts.length} Projects Analyzed
                    </span>
                    <TabHelpButton tabKey="forecasts" />
                </div>
            </div>

            {/* Top Scorecards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-5 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 shadow-xs flex items-center justify-between hover:border-emerald-500/50 transition">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-400">High Confidence (&gt;90%)</div>
                        <div className="text-3xl font-black text-emerald-600 dark:text-emerald-400 mt-1 font-headline">
                            {highCount} Projects
                        </div>
                        <div className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold mt-1">
                            Predictable milestone velocity
                        </div>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-neutral-900 text-emerald-500 flex items-center justify-center text-xl">
                        <CheckCircleOutlined />
                    </div>
                </div>

                <div className="p-5 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 shadow-xs flex items-center justify-between hover:border-blue-500/50 transition">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Moderate Variance (70–90%)</div>
                        <div className="text-3xl font-black text-blue-600 dark:text-[#00AAF1] mt-1 font-headline">
                            {medCount} Projects
                        </div>
                        <div className="text-xs text-blue-600 dark:text-[#00AAF1] font-semibold mt-1">
                            Review latency tracking required
                        </div>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-neutral-900 text-blue-500 flex items-center justify-center text-xl">
                        <CompassOutlined />
                    </div>
                </div>

                <div className="p-5 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 shadow-xs flex items-center justify-between hover:border-amber-500/50 transition">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Low / At Risk (&lt;70%)</div>
                        <div className="text-3xl font-black text-amber-500 mt-1 font-headline">
                            {lowCount} Projects
                        </div>
                        <div className="text-xs text-amber-500 font-semibold mt-1">
                            Critical bottlenecks detected
                        </div>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-amber-50 dark:bg-neutral-900 text-amber-500 flex items-center justify-center text-xl">
                        <WarningOutlined />
                    </div>
                </div>
            </div>

            {/* Visual Charts Grid: Donut + Confidence Cone */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Donut Chart: Model Confidence Distribution */}
                <div className={card}>
                    <div className="mb-4">
                        <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                            Model Confidence Distribution
                        </h3>
                        <p className="text-xs text-slate-400">
                            Delivery predictability across active portfolio
                        </p>
                    </div>

                    <div className="relative h-60">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={confidencePieData}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius="60%"
                                    outerRadius="88%"
                                    paddingAngle={3}
                                    stroke="none"
                                >
                                    {confidencePieData.map((d) => (
                                        <Cell key={d.name} fill={d.fill} />
                                    ))}
                                </Pie>
                                <Tooltip {...tooltipStyle} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            <span className="text-2xl font-black text-slate-900 dark:text-white font-headline">
                                {forecasts.length > 0 ? Math.round(forecasts.reduce((a, b) => a + (b.confidenceScore || 85), 0) / forecasts.length) : 0}%
                            </span>
                            <span className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider">Avg Confidence</span>
                        </div>
                    </div>

                    <div className="space-y-2 text-xs font-semibold mt-3">
                        {confidencePieData.map((d) => (
                            <div key={d.name} className="flex justify-between items-center text-slate-700 dark:text-slate-300">
                                <span className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.fill }} />
                                    <span>{d.name}</span>
                                </span>
                                <span className="font-extrabold text-slate-900 dark:text-white">
                                    {d.value} ({forecasts.length > 0 ? Math.round((d.value / forecasts.length) * 100) : 0}%)
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Composed Chart: Delivery Confidence Cone (P50 vs P80 vs Optimistic) */}
                <div className={`${card} lg:col-span-2`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                        <div>
                            <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                                Predictive Delivery Cone (P50 vs P80 Variance Days)
                            </h3>
                            <p className="text-xs text-slate-400">
                                Simulated delivery slippage envelope in days beyond target due date
                            </p>
                        </div>
                        <span className="text-xs font-semibold text-slate-400 bg-slate-100 dark:bg-neutral-900 px-2.5 py-1 rounded-lg">
                            Hover for project drill-down
                        </span>
                    </div>

                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={varianceConeData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="p80Grad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={C.amber} stopOpacity={0.4} />
                                        <stop offset="95%" stopColor={C.amber} stopOpacity={0.05} />
                                    </linearGradient>
                                    <linearGradient id="p50Grad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={C.blue} stopOpacity={0.5} />
                                        <stop offset="95%" stopColor={C.blue} stopOpacity={0.1} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.2} vertical={false} />
                                <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                                <YAxis tick={axisTick} axisLine={false} tickLine={false} unit="d" />
                                <Tooltip content={<ConeTooltip />} />
                                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                                <Area
                                    type="monotone"
                                    dataKey="p80Days"
                                    name="P80 Conservative (Days Slip)"
                                    stroke={C.amber}
                                    strokeWidth={2}
                                    fillOpacity={1}
                                    fill="url(#p80Grad)"
                                />
                                <Area
                                    type="monotone"
                                    dataKey="p50Days"
                                    name="P50 Expected (Days Slip)"
                                    stroke={C.blue}
                                    strokeWidth={2}
                                    fillOpacity={1}
                                    fill="url(#p50Grad)"
                                />
                                <Line
                                    type="monotone"
                                    dataKey="optimisticDays"
                                    name="Optimistic Velocity (Days)"
                                    stroke={C.green}
                                    strokeWidth={2.5}
                                    dot={{ r: 4, fill: C.green }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Row 2: Critical Path Risk Score vs Velocity Progress Composed Chart */}
            <div className={card}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                    <div>
                        <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                            Current Completion Progress vs Risk Score Quadrant
                        </h3>
                        <p className="text-xs text-slate-400">
                            Comparison between actual progress percentage (Bars) and critical path risk score (Line)
                        </p>
                    </div>
                </div>

                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={varianceConeData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.2} vertical={false} />
                            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                            <YAxis tick={axisTick} axisLine={false} tickLine={false} unit="%" />
                            <Tooltip content={<ConeTooltip />} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar
                                dataKey="progress"
                                name="Velocity Completion %"
                                fill={C.blue}
                                radius={[6, 6, 0, 0]}
                                maxBarSize={48}
                            />
                            <Line
                                type="monotone"
                                dataKey="riskScore"
                                name="Risk Score (0-100)"
                                stroke={C.red}
                                strokeWidth={2.5}
                                dot={{ r: 4, fill: C.red }}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Interactive Project Forecast Cards with Rich Hover-Over Details */}
            <div className={card}>
                <div className="mb-4">
                    <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                        Project-by-Project Delivery Risk &amp; Bottleneck Diagnostics
                    </h3>
                    <p className="text-xs text-slate-400">
                        Interactive diagnostic cards with confidence grades, variance projections, and AI recommendations
                    </p>
                </div>

                {forecasts.length === 0 ? (
                    <div className="py-12 text-center text-slate-400 text-sm">
                        No project forecasts available for the selected filters.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {forecasts.map((f) => {
                        const conf = f.confidenceScore || 85;
                        const isHigh = conf >= 90;
                        const isMed = conf >= 70 && conf < 90;
                        const confColor = isHigh
                            ? 'text-emerald-500 bg-emerald-500/10 border-emerald-500/30'
                            : isMed
                            ? 'text-[#00AAF1] bg-[#00AAF1]/10 border-[#00AAF1]/30'
                            : 'text-amber-500 bg-amber-500/10 border-amber-500/30';

                        return (
                            <div
                                key={f.id}
                                className="p-5 rounded-2xl bg-slate-50/70 dark:bg-neutral-950 border border-slate-200/60 dark:border-neutral-800 hover:border-blue-500/60 transition-all duration-200 hover:shadow-lg space-y-4"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <h4 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white font-headline">
                                            {f.projectName}
                                        </h4>
                                        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400 mt-1">
                                            <span>Target: <strong className="text-slate-700 dark:text-slate-200">{f.targetDueDate ? moment(f.targetDueDate).format('MMM D, YYYY') : 'Not Set'}</strong></span>
                                            <span>•</span>
                                            <span>Predicted: <strong className="text-blue-500">{f.predictedCompletionDate ? moment(f.predictedCompletionDate).format('MMM D, YYYY') : 'Calculating'}</strong></span>
                                        </div>
                                    </div>
                                    <span className={`text-xs font-black px-2.5 py-1 rounded-xl border shrink-0 ${confColor}`}>
                                        {conf}% Confidence
                                    </span>
                                </div>

                                {/* Metric indicators */}
                                <div className="flex items-center justify-between p-3 rounded-xl bg-white dark:bg-black border border-slate-200/70 dark:border-neutral-800">
                                    <div className="space-y-0.5">
                                        <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Velocity Progress</div>
                                        <div className="text-xl font-black text-blue-500 font-headline">{f.currentProgress}%</div>
                                    </div>
                                    <div className="text-right space-y-0.5">
                                        <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Projected Variance</div>
                                        <div className={`text-base font-black ${f.varianceDays > 5 ? 'text-amber-500' : 'text-emerald-500'}`}>
                                            {f.varianceDays ? `+${f.varianceDays} Days` : 'On Schedule'}
                                        </div>
                                    </div>
                                </div>

                                {/* Active Bottleneck & AI Pacing Recommendation */}
                                <div className="p-3 rounded-xl bg-white dark:bg-black border border-slate-200/60 dark:border-neutral-800 text-xs text-slate-600 dark:text-slate-300 space-y-1.5">
                                    <div className="flex items-start gap-2">
                                        <ThunderboltOutlined className="text-amber-500 mt-0.5 shrink-0" />
                                        <span><strong>Critical Path Bottleneck:</strong> {f.bottlenecks || f.bottleneckSummary || 'None detected'}</span>
                                    </div>
                                    <div className="text-slate-400 italic text-[11px] pl-5">
                                        💡 {f.recommendation}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    </div>
                )}
            </div>
            </div>
        </Spin>
    );
}
